<template>
  <!-- Plus's sheet, on a phone: a line of its list. -->
  <v-list-item
    v-if="variant === 'row' && shown && wallet.data.value"
    :to="{ name: 'settings:billing' }"
    :aria-label="t('core.navigation.creditsTooltip', { amount: balance })"
    class="credits-row"
    :class="`credits-row--${tone}`"
    slim
    @click="emit('navigate')"
  >
    <template #prepend>
      <v-icon :icon="mdiWalletOutline" size="20" class="credits-row__icon" />
    </template>
    <v-list-item-title>{{ t('core.navigation.credits') }}</v-list-item-title>
    <template #append>
      <span class="credits-row__amount">{{ balance }}</span>
    </template>
  </v-list-item>
  <v-tooltip v-else-if="shown && wallet.data.value" location="end" :open-delay="500">
    <template #activator="{ props: activator }">
      <router-link
        v-bind="activator"
        :to="{ name: 'settings:billing' }"
        :aria-label="t('core.navigation.creditsTooltip', { amount: balance })"
        class="credits-chip"
        :class="`credits-chip--${tone}`"
      >
        <v-icon :icon="mdiWalletOutline" size="18" class="credits-chip__icon" />
        <span class="credits-chip__amount">{{ balance }}</span>
        <span class="credits-chip__label">{{ t('core.navigation.credits') }}</span>
      </router-link>
    </template>
    <span class="text-caption">{{ t('core.navigation.creditsTooltip', { amount: balance }) }}</span>
  </v-tooltip>
</template>

<script setup lang="ts">
/**
 * The balance in the navigation rail, for billing managers on a platform that sells credits
 * (`docs/plans/billing-ui.md` U5, board `Main`). Coloured by the runway the server computes —
 * the same thresholds as the balance banners — and a way into the wallet's settings. On a phone
 * the same balance is a line of Plus's sheet (`docs/plans/mobile-navigation.md` MN3).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiWalletOutline } from '@mdi/js'
import { canSeeWallet, useAutoTopUp, useWallet } from '../queries'
import { useMoney } from '../composables/useMoney'
import { runwayTone } from '../banners'

withDefaults(defineProps<{
  /** `rail`: the navigation rail's entry; `row`: a line of Plus's sheet on a phone. */
  variant?: 'rail' | 'row'
}>(), {
  variant: 'rail',
})

const emit = defineEmits<{
  // The row was followed: the sheet holding it closes.
  navigate: []
}>()

const { t } = useI18n()
const { price } = useMoney()

const shown = computed(() => canSeeWallet())
const wallet = useWallet()
const autoTopUp = useAutoTopUp()

const balance = computed(() =>
  price(wallet.data.value?.balance_cents ?? 0, wallet.data.value?.currency ?? 'EUR'),
)
const tone = computed(() =>
  wallet.data.value ? runwayTone(wallet.data.value, autoTopUp.data.value?.enabled === true) : 'fine',
)
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.credits-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  width: 56px;
  margin-bottom: 1rem;
  padding: $space-xs 0;
  border-radius: $radius-md;
  text-decoration: none;
  color: rgba(var(--v-theme-on-surface), $opacity-secondary);
  @include transition-fast;

  &:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 2px;
  }

  &__amount {
    font-size: 0.6875rem;
    font-weight: $font-semibold;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__label {
    font-size: 0.625rem;
    font-weight: $font-medium;
    letter-spacing: 0.02em;
  }

  &--low,
  &--critical {
    background: rgba(var(--v-theme-warning), 0.12);
    color: rgb(var(--v-theme-warning));
  }

  &--empty {
    background: rgba(var(--v-theme-error), 0.12);
    color: rgb(var(--v-theme-error));
  }
}

.credits-row {
  &__amount {
    font-size: $text-sm;
    font-weight: $font-semibold;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &--low,
  &--critical {
    .credits-row__icon,
    .credits-row__amount {
      color: rgb(var(--v-theme-warning));
    }
  }

  &--empty {
    .credits-row__icon,
    .credits-row__amount {
      color: rgb(var(--v-theme-error));
    }
  }
}
</style>
